import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from 'src/api/requests';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Option } from 'src/components/SelectBox';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Organization } from 'src/interfaces/organization.interface';
import { getHttpStatus } from 'src/utils/requestUtils';

export const Organizations = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();
  const navigate = useNavigate();
  const firebaseUser = useFirebaseAuth();

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Option | undefined>(undefined);
  const [pendingOrganization, setPendingOrganization] = useState<string | null>(
    null,
  );
  const [newOrganizationName, setNewOrganizationName] = useState('');

  const userId = firebaseUser?.appUserId;

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    (async () => {
      try {
        const orgs: Organization[] = await get(
          `/users/${userId}/organizations`,
        );
        setOrganizations(orgs);
      } catch {
        notification.error('Failed to fetch organizations');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, get, notification]);

  useEffect(() => {
    if (firebaseUser?.organization) {
      setCurrentOrg({
        label: firebaseUser.organization?.name + ' (current)' || '',
        value: firebaseUser.organization?.id || '',
      });
    }
  }, [firebaseUser?.organization]);

  const handleChangeOrganization = (orgId: string) => {
    if (orgId === currentOrg?.value) {
      setPendingOrganization(null);
      return;
    }

    setPendingOrganization(orgId);
  };

  const handleConfirmSwitch = () => {
    if (!pendingOrganization) {
      navigate('/');
      return;
    }

    // Use the org-switch route which handles the organization switching logic
    navigate(`/org-switch?orgId=${pendingOrganization}&returnTo=/`);
    setPendingOrganization(null);
  };

  const handleCreateOrganization = async () => {
    if (!newOrganizationName.trim()) {
      notification.error('Please enter an organization name');
      return;
    }

    setCreating(true);
    try {
      const organization = await post('/organizations', {
        name: newOrganizationName.trim(),
      });

      navigate(`/org-switch?orgId=${organization.id}&returnTo=/`);
      notification.success('Organization created successfully!');
    } catch (error) {
      console.error('Error creating organization:', error);
      const statusCode = getHttpStatus(error);

      if (statusCode && statusCode >= 500) {
        notification.error(
          'Something went wrong. Please try again in a moment.',
        );
      } else {
        notification.error('Something went wrong. Please contact support.');
      }
    } finally {
      setCreating(false);
    }
  };

  const fetchOrganizationsOptions = async (query: string) => {
    return organizations
      .filter((org) => org.name.toLowerCase().includes(query.toLowerCase()))
      .map((org) => ({
        label: org.name,
        value: org.id,
      }));
  };

  const hasOrganizations = organizations.length > 0;

  return (
    <div className="flex-1">
      <Heading
        title="Organizations"
        subtitle={
          hasOrganizations
            ? 'Select your organization'
            : 'You need to create an organization to continue'
        }
      />

      {loading && <Loading className="p-4" />}

      {!loading && (
        <ContentWrapper>
          <div className="max-w-md">
            {hasOrganizations ? (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-600 mb-2">
                    Select Organization
                  </label>
                  <AutoCompleteSelectBox
                    key={
                      String(currentOrg?.value) +
                      '-' +
                      String(currentOrg?.label)
                    }
                    fetchOptions={fetchOrganizationsOptions}
                    onChange={(value) => handleChangeOrganization(value)}
                    className="w-full"
                    variant="outlined"
                    size="medium"
                    disabled={loading}
                    defaultOptions={organizations.map((org) => ({
                      label:
                        org.name +
                        (org.id === currentOrg?.value ? ' (current)' : ''),
                      value: org.id,
                    }))}
                    allowRemoveOption={false}
                    placeholder="Select Organization..."
                    defaultValue={currentOrg}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleConfirmSwitch}
                    disabled={loading || !pendingOrganization}
                    color="primary"
                    className="flex-1"
                  >
                    Continue
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-4">
                <p className="text-neutral-600 mb-4">
                  You are not currently a member in any organizations. To
                  continue using this platform, please create a new
                  organization.
                </p>
                <div className="space-y-4">
                  <Input
                    label="Organization Name"
                    value={newOrganizationName}
                    onChange={(e) => setNewOrganizationName(e.target.value)}
                    placeholder="Enter organization name"
                    required
                    disabled={creating}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newOrganizationName.trim()) {
                        handleCreateOrganization();
                      }
                    }}
                  />
                  <Button
                    onClick={handleCreateOrganization}
                    disabled={creating || !newOrganizationName.trim()}
                    color="primary"
                    className="w-full"
                  >
                    {creating ? 'Creating...' : 'Create New Organization'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ContentWrapper>
      )}
    </div>
  );
};
